import * as React from "react";
import {
    Button,
    makeStyles,
    Text,
    tokens,
    ProgressBar,
    Badge,
    Tooltip,
    Spinner,
} from "@fluentui/react-components";
import {
    DismissRegular,
    ChevronRightRegular,
    ChevronDownRegular,
    ChevronDoubleDownRegular,
    ChevronDoubleUpRegular,
    ScanDashFilled,
    ScanTextFilled,
    MailInboxAllRegular,
    DocumentSearchRegular,
    HistoryRegular,
} from "@fluentui/react-icons";
import { useTabStore } from "../../providers/TabProvider";
import { threatConfig, ThreatType } from "../../components/threat/threat.config";
import { getScanList } from "../../api";
import { ScanHistoryItem } from "../../types";

const useStyles = makeStyles({
    container: {
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        backgroundColor: tokens.colorNeutralBackground1,
        height: "100vh",
        boxSizing: "border-box",
        overflowY: "hidden", // Prevent main scroll
    },
    headerButton: {
        width: "100%",
        backgroundColor: "#0F6CBD",
        border: "none",
        color: "white",
        ":hover": {
            backgroundColor: "#0E5EA8",
            color: "white",
        },
        flexShrink: 0,
    },
    scanSection: {
        display: "flex",
        flexDirection: "column",
        flex: "0 1 auto", // flexible height
        minHeight: 0, // Allow shrinking
        gap: "8px",
        transition: "flex 0.3s ease",
    },
    historySection: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto", // Take remaining space
        minHeight: 0, // Allow shrinking
        gap: "8px",
        transition: "flex 0.3s ease",
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
        cursor: "pointer",
        userSelect: "none",
        ":hover": {
            opacity: 0.8,
        }
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontWeight: 600,
    },
    scrollableList: {
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        paddingRight: "4px", // For scrollbar space
        // flexGrow: 1, // Removed to allow auto-height
        // Custom thin scrollbar
        "::-webkit-scrollbar": {
            width: "6px",
        },
        "::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "::-webkit-scrollbar-thumb": {
            backgroundColor: tokens.colorNeutralStroke1, // "rgba(0, 0, 0, 0.3)" or similar
            borderRadius: "4px",
        },
        "::-webkit-scrollbar-thumb:hover": {
            backgroundColor: tokens.colorNeutralStroke1Hover,
        },
    },
    card: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "8px",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: tokens.colorNeutralBackground1,
        flexShrink: 0, // Prevent card from shrinking
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: "14px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "165px",
    },
    cardSubtitle: {
        color: tokens.colorNeutralForeground2,
        fontSize: "12px",
    },
    progressBar: {
        height: "4px",
        "& .fui-ProgressBar__bar": {
            backgroundColor: "#0F6CBD",
        },
    },
    progressText: {
        fontSize: "10px",
        color: tokens.colorNeutralForeground2,
    },
    historyItem: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "8px",
        padding: "12px 12px 12px 8px", // Reduced left padding to bring icon closer to edge if needed
        display: "flex",
        flexDirection: "row",
        gap: "8px", // Reduced gap between icon and content
        cursor: "pointer",
        flexShrink: 0,
        alignItems: "flex-start", // Align items to top to match title row
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground2,
        }
    },
    historyItemContent: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        gap: "4px",
        overflow: "hidden",
    },
    historyHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center", // Center align logically with icon
        width: "100%",
        minHeight: "20px", // Ensure min height for alignment
    },
    historyFooter: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        gap: "8px",
        border: `1px dashed ${tokens.colorNeutralStroke1}`,
        borderRadius: "8px",
        textAlign: "center",
        color: tokens.colorNeutralForeground3,
    },
    emptyStateText: {
        fontSize: "12px",
    },
    showMoreButton: {
        width: "100%",
        backgroundColor: tokens.colorBrandBackground2,
        border: "none",
        color: tokens.colorBrandForeground1,
        borderRadius: "8px",
        justifyContent: "center",
        gap: "6px",
        fontWeight: 600,
        ":hover": {
            backgroundColor: tokens.colorBrandBackground2Hover,
            color: tokens.colorBrandForeground1,
        },
    },
});

const SeverityIcon = ({ severity }: { severity: ThreatType }) => {
    const config = threatConfig[severity] || threatConfig.default;
    const Icon = config.icon;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const backgroundColor = (config as any).bgColor || (config as any).bgcolor;

    return (
        <Tooltip content={config.title} relationship="label">
            <div style={{
                backgroundColor,
                color: config.color,
                width: "24px", // Smaller size
                height: "24px", // Smaller size
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px", // Smaller icon size
                flexShrink: 0,
                marginTop: "0px", // Align with text
            }}>
                <Icon />
            </div>
        </Tooltip>
    );
};

export default function OverviewTab() {
    const styles = useStyles();
    const { setActiveTab, setSelectedHistoryItem, emailData } = useTabStore((s) => s);

    // State for Collapsible Sections
    const [isActiveScansOpen, setIsActiveScansOpen] = React.useState(true);
    const [isHistoryOpen, setIsHistoryOpen] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(true);
    const [showAllActiveScans, setShowAllActiveScans] = React.useState(false);

    const ACTIVE_SCANS_LIMIT = 5;

    const [activeScans, setActiveScans] = React.useState<ScanHistoryItem[]>([]);
    const [historyScans, setHistoryScans] = React.useState<ScanHistoryItem[]>([]);

    React.useEffect(() => {
        const fetchScans = async () => {
            setIsLoading(true);
            const result = await getScanList({ emailData });
            if (Array.isArray(result)) {
                setActiveScans(result.filter((item: ScanHistoryItem) => item.scan_status === "ongoing"));
                setHistoryScans(result.filter((item: ScanHistoryItem) => item.scan_status === "complete"));
            }
            setIsLoading(false);
        };

        fetchScans();
        // Optional: Polling or refresh on focus
    }, []);

    // Filter out the current email from history scans
    const filteredHistoryScans = historyScans.filter(
        (scan) => scan.email_fingerprint !== emailData?.emailFingerprint
    );

    return (
        <div className={styles.container}>
            <Button
                className={styles.headerButton}
                icon={<ScanTextFilled />}
                size="large"
                onClick={() => setActiveTab("hero-scan-section")} /* Go to scan */
            >
                Scan Current Email
            </Button>

            {/* Active Scans Section */}
            <div className={styles.scanSection} style={{ flex: isActiveScansOpen ? "0 1 auto" : "0 0 auto" }}>
                <div
                    className={styles.sectionHeader}
                    onClick={() => setIsActiveScansOpen(!isActiveScansOpen)}
                >
                    <div className={styles.sectionTitle}>
                        {isActiveScansOpen ? <ChevronDownRegular /> : <ChevronRightRegular />}
                        <ScanDashFilled fontSize={20} color={"#0A78D5"} />
                        <Text weight="semibold">Active Scans</Text>
                        <Badge appearance="outline" color="informative" shape="circular" title="Active Scans - Other Emails Scan">
                            i
                        </Badge>
                    </div>
                    <Badge appearance="filled" color="informative" shape="rounded">
                        {activeScans.length}
                    </Badge>
                </div>

                {isActiveScansOpen && (
                    <div className={styles.scrollableList}>
                        {isLoading ? (
                            <div className={styles.emptyState} style={{ border: 'none' }}>
                                <Spinner size="medium" />
                            </div>
                        ) : activeScans.length === 0 ? (
                            <div className={styles.emptyState}>
                                <DocumentSearchRegular fontSize={32} />
                                <Text weight="semibold">No active scans running</Text>
                                <Text className={styles.emptyStateText}>Hit the button above to scan your current email.</Text>
                            </div>
                        ) : (
                            <>
                                {(showAllActiveScans ? activeScans : activeScans.slice(0, ACTIVE_SCANS_LIMIT)).map((scan) => (
                                    <div key={scan.group_id} className={styles.card}>
                                        <div className={styles.cardHeader}>
                                            <div>
                                                <Text block className={styles.cardTitle}>
                                                    {scan.subject}
                                                </Text>
                                                <Text block className={styles.cardSubtitle}>
                                                    {scan.sender}
                                                </Text>
                                            </div>
                                            {/* <DismissRegular style={{ color: tokens.colorNeutralForeground3, cursor: "pointer" }} /> */}
                                        </div>
                                        <div>
                                            <ProgressBar className={styles.progressBar} />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                                <Text className={styles.progressText}>{scan.scan_status}</Text>
                                                {/* <Text className={styles.progressText}>{Math.round((scan.tasks_count / 10) * 100)}%</Text> */}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {activeScans.length > ACTIVE_SCANS_LIMIT && (
                                    <Button
                                        className={styles.showMoreButton}
                                        appearance="subtle"
                                        size="medium"
                                        icon={showAllActiveScans ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />}
                                        iconPosition="after"
                                        onClick={() => setShowAllActiveScans((prev) => !prev)}
                                    >
                                        {showAllActiveScans ? "Show Less" : "Show More"}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* History Section */}
            <div className={styles.historySection} style={{ flex: isHistoryOpen ? "0 1 auto" : "0 0 auto" }}>
                <div
                    className={styles.sectionHeader}
                    onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                >
                    <div className={styles.sectionTitle}>
                        {isHistoryOpen ? <ChevronDownRegular /> : <ChevronRightRegular />}
                        <MailInboxAllRegular fontSize={20} color={"#0A78D5"} />
                        <Text weight="semibold">History</Text>
                        <Badge appearance="outline" color="informative" shape="circular" title="History - Previous Scan Result">
                            i
                        </Badge>
                    </div>
                    <Badge appearance="filled" color="informative" shape="rounded">{filteredHistoryScans.length}</Badge>
                </div>

                {isHistoryOpen && (
                    <div className={styles.scrollableList}>
                        {isLoading ? (
                            <div className={styles.emptyState} style={{ border: 'none' }}>
                                <Spinner size="medium" />
                            </div>
                        ) : filteredHistoryScans.length === 0 ? (
                            <div className={styles.emptyState}>
                                <HistoryRegular fontSize={32} />
                                <Text weight="semibold">No history yet</Text>
                                <Text className={styles.emptyStateText}>Your scan history will appear here once you start scanning.</Text>
                            </div>
                        ) : (
                            filteredHistoryScans.map((scan) => (
                                <div
                                    key={scan.group_id}
                                    className={styles.historyItem}
                                    // ...
                                    onClick={() => {
                                        setSelectedHistoryItem(scan);
                                        setActiveTab("history-result");
                                    }}
                                >
                                    <SeverityIcon severity={scan.overall_status} />
                                    <div className={styles.historyItemContent}>
                                        <div className={styles.historyHeader}>
                                            <Text block className={styles.cardTitle} title={scan.subject}>
                                                {scan.subject}
                                            </Text>
                                            <Text className={styles.cardSubtitle} style={{ marginLeft: "auto" }}>
                                                {new Date(scan.timestamp_requested).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Text>
                                        </div>
                                        <div className={styles.historyFooter}>
                                            <Text block className={styles.cardSubtitle}>
                                                {scan.sender}
                                            </Text>
                                            <ChevronRightRegular style={{ color: tokens.colorNeutralForeground3 }} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div >
        </div >
    );
}

